import { useNavigate, useLocation } from "@tanstack/react-router";
import ModalOverlayLoadingSpinner from "components/loading/ModalOverlayLoadingSpinner";
import { useTranslation } from "react-i18next";
import { useProduct } from "contexts/ProductContext";
import { useProject } from "contexts/ProjectContext";
import { Button } from "components/button";
import { routePath } from "shared/constants";
import { useState } from "react";
import { ErrorPage } from "components/errors/ErrorPage";
import { ProductNavigationBar } from "./components/ProductNavigationBar";
import { NoProductFound } from "./components/NoProductFound";

export const ProductDetails: React.FC = () => {
  const { t } = useTranslation(["productDetails"]);
  const navigate = useNavigate();
  const from = useLocation({ select: (location) => location.state.from });
  const [fromUrl, setFromUrl] = useState(from);

  const { product, loading, error } = useProduct();
  const { project } = useProject();

  // Keeps the origin page across tab changes, which navigate without state.
  if (from && from !== fromUrl) {
    setFromUrl(from);
  }

  const handleGoBack = () => {
    if (fromUrl) {
      navigate({ href: fromUrl });
    } else if (project) {
      navigate({
        to: "/projects/$projectId",
        params: { projectId: project.id },
      });
    } else {
      navigate({ to: "/books" });
    }
  };

  if (error) {
    return (
      <ErrorPage
        title={t("productDetails:notFound.title")}
        message={t("productDetails:notFound.message")}
        backLink={
          project
            ? routePath("/projects/$projectId", { projectId: project.id })
            : routePath("/books")
        }
      />
    );
  }

  if (!product && !loading) {
    return <NoProductFound />;
  }

  return (
    <>
      {project ? (
        <Button
          variant="ghost"
          label={t("productDetails:header.overview")}
          icon="arrow-left"
          className="-ml-4"
          onClick={handleGoBack}
          analyticsId="button_goto_overview"
          data-testid="overview-link"
        />
      ) : (
        <Button
          variant="ghost"
          label={t("productDetails:header.books")}
          icon="arrow-left"
          className="-ml-4"
          onClick={handleGoBack}
          analyticsId="button_goto_overview"
          data-testid="books-link"
        />
      )}

      <ModalOverlayLoadingSpinner isLoading={loading} />

      <ProductNavigationBar />
    </>
  );
};
